import { Badge } from '@radix-ui/themes'
import { ComponentProps } from 'react'
import { PamPlayerStatus, PamPlayerVerificationStatus } from '@Generated/back-office-client/src'
import { getStatusDisplay } from './pam-verification-status-display'

type BadgeColor = ComponentProps<typeof Badge>['color']

export type AccountStatusDisplay = { label: string; color: BadgeColor }

const ACCOUNT_STATUS_DISPLAY: Record<string, AccountStatusDisplay> = {
  [PamPlayerStatus.active]: { label: 'Active', color: 'green' },
  [PamPlayerStatus.probationary]: { label: 'Probationary', color: 'amber' },
  [PamPlayerStatus.suspended]: { label: 'Suspended', color: 'red' },
  [PamPlayerStatus.self_excluded]: { label: 'Self-excluded', color: 'gray' },
  [PamPlayerStatus.deleted]: { label: 'Deleted', color: 'gray' },
}

export const getAccountStatusDisplay = (status: string): AccountStatusDisplay => {
  return ACCOUNT_STATUS_DISPLAY[status] ?? { label: status, color: 'gray' }
}

export type BannerDisplay = { color: BadgeColor; attention: boolean }

// Verification states that warrant a back-office "needs attention" treatment when the account
// is not yet operationally cleared (active). submitted/not_started/approved/manually_approved are calm.
const ATTENTION_VERIFICATION_STATUSES: ReadonlySet<string> = new Set([
  PamPlayerVerificationStatus.rejected,
  PamPlayerVerificationStatus.locked,
  PamPlayerVerificationStatus.denied,
  PamPlayerVerificationStatus.duplicate_review,
])

/**
 * Drives the player-details KYC banner. Once the account is active (operationally cleared —
 * including manually-approved duplicates) the banner is calm green regardless of the frozen
 * verification status. Otherwise it follows the verification status's severity.
 */
export const getBannerDisplay = (
  accountStatus: string,
  verificationStatus: string,
): BannerDisplay => {
  if (accountStatus === PamPlayerStatus.active) {
    return { color: 'green', attention: false }
  }
  return {
    color: getStatusDisplay(verificationStatus).color,
    attention: ATTENTION_VERIFICATION_STATUSES.has(verificationStatus),
  }
}
